<template>
  <div class="audit-log-page space-y-6 animate-[fadeIn_0.6s_ease-out]">
    <!-- 页面标题 -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F5D76E] to-[#F2C94C] flex items-center justify-center shadow-sm">
          <svg class="w-5 h-5 text-[#1B3C59]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <div>
          <h3 class="text-xl font-black text-slate-900">审计日志</h3>
        </div>
      </div>
      <button
        @click="handleExport"
        class="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        导出 JSON
      </button>
    </div>

    <!-- 前台关联提示 -->
    <div class="bg-blue-50/80 backdrop-blur-sm border border-blue-100/60 rounded-2xl p-4 flex items-start gap-3 text-sm text-blue-700">
      <svg class="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div>
        <p class="font-medium mb-1">前台关联</p>
        <p class="text-blue-600">操作日志记录所有后台管理行为，用于追溯内容变更和安全审计</p>
      </div>
    </div>

    <!-- 筛选栏 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm p-5 transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-form :inline="true" size="small">
        <el-form-item label="操作类型">
          <el-select v-model="filter.action" clearable placeholder="全部">
            <el-option label="创建" value="CREATE" />
            <el-option label="更新" value="UPDATE" />
            <el-option label="删除" value="DELETE" />
            <el-option label="配置变更" value="CONFIG_CHANGE" />
          </el-select>
        </el-form-item>
        <el-form-item label="对象类型">
          <el-select v-model="filter.targetType" clearable placeholder="全部">
            <el-option label="专家" value="expert" />
            <el-option label="话题" value="topic" />
            <el-option label="案例" value="case" />
            <el-option label="配置" value="config" />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="filter.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始"
            end-placeholder="结束"
            value-format="timestamp"
          />
        </el-form-item>
        <el-form-item>
          <button
            @click="applyFilter"
            class="px-4 py-1.5 bg-[#F2C94C] text-white text-sm font-medium rounded-full hover:bg-[#E5B73B] transition-colors"
          >
            筛选
          </button>
          <button
            @click="resetFilter"
            class="ml-2 px-4 py-1.5 border border-slate-200 text-slate-600 text-sm rounded-xl hover:bg-slate-50 transition-colors"
          >
            重置
          </button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 日志列表 -->
    <div class="bg-white/60 backdrop-blur-xl border border-white/80 rounded-3xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md hover:border-[#F2C94C]/20">
      <el-table :data="filteredLogs" stripe size="small" style="width: 100%">
        <el-table-column prop="id" label="日志ID" width="160" />
        <el-table-column label="时间" width="160">
          <template #default="{ row }">
            {{ formatTime(row.timestamp) }}
          </template>
        </el-table-column>
        <el-table-column label="操作人" width="100">
          <template #default="{ row }">
            <span class="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded">{{ row.actor?.role || 'unknown' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template #default="{ row }">
            <span
              :class="[
                'inline-flex items-center px-2 py-0.5 text-xs rounded font-medium',
                actionClass(row.action)
              ]"
            >
              {{ actionLabel(row.action) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="对象" width="120">
          <template #default="{ row }">
            {{ row.target?.type }} / {{ row.target?.id }}
          </template>
        </el-table-column>
        <el-table-column label="字段" width="120">
          <template #default="{ row }">
            {{ row.target?.field || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="变更前值">
          <template #default="{ row }">
            <span class="font-mono text-xs text-red-500 line-through">{{ formatValue(row.before) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="变更后值">
          <template #default="{ row }">
            <span class="font-mono text-xs text-emerald-500">{{ formatValue(row.after) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="理由" width="180">
          <template #default="{ row }">
            {{ row.reason || '-' }}
          </template>
        </el-table-column>
      </el-table>

      <!-- 空状态 -->
      <div v-if="filteredLogs.length === 0" class="text-center py-12">
        <svg class="w-12 h-12 text-slate-200 mx-auto mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div class="text-slate-400 text-sm">暂无审计日志</div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAuditLogs, exportAuditLogs } from '../../services/audit'

export default {
  name: 'AuditLog',

  data() {
    return {
      logs: [],
      filter: {
        action: '',
        targetType: '',
        dateRange: null,
      },
    }
  },

  computed: {
    filteredLogs() {
      return this.logs
    },
  },

  created() {
    this.loadLogs()
  },

  methods: {
    loadLogs() {
      this.logs = getAuditLogs()
    },

    applyFilter() {
      const filters = {}
      if (this.filter.action) filters.action = this.filter.action
      if (this.filter.targetType) filters.targetType = this.filter.targetType
      if (this.filter.dateRange) {
        filters.startTime = this.filter.dateRange[0]
        filters.endTime = this.filter.dateRange[1] + 24 * 60 * 60 * 1000 - 1
      }
      this.logs = getAuditLogs(filters)
    },

    resetFilter() {
      this.filter = { action: '', targetType: '', dateRange: null }
      this.loadLogs()
    },

    formatTime(ts) {
      if (!ts) return '-'
      const d = new Date(ts)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },

    actionClass(action) {
      const map = {
        CREATE: 'bg-emerald-50 text-emerald-600',
        UPDATE: 'bg-amber-50 text-amber-600',
        DELETE: 'bg-red-50 text-red-600',
        CONFIG_CHANGE: 'bg-blue-50 text-blue-600',
      }
      return map[action] || 'bg-slate-100 text-slate-500'
    },

    actionLabel(action) {
      const map = {
        CREATE: '创建',
        UPDATE: '更新',
        DELETE: '删除',
        CONFIG_CHANGE: '配置变更',
      }
      return map[action] || action
    },

    formatValue(val) {
      if (val === null || val === undefined) return '-'
      if (typeof val === 'boolean') return val ? '是' : '否'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    },

    handleExport() {
      const json = exportAuditLogs()
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${Date.now()}.json`
      a.click()
      URL.revokeObjectURL(url)
      this.$message.success('导出成功')
    },
  },
}
</script>
